/* fourpaw-standard: lifecycle-delegated */
(() => {
  const gidNumber = (value) => Number(String(value || '').split('/').pop()) || 0;
  const metadataFor = (item) => {
    const raw = item?.properties?.__kaching_bundles;
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      return null;
    }
  };
  const readDeal = (root) => {
    const element = root.querySelector('kaching-bundles-block[deal-block]');
    if (!element) return null;
    try {
      const deal = JSON.parse(element.getAttribute('deal-block') || '{}');
      return deal && typeof deal === 'object' ? deal : null;
    } catch (error) {
      return null;
    }
  };
  const realGiftEntries = (state, kaching, identities = []) => {
    const ids = kaching.realGiftIds?.[state?.kind] || {};
    return identities.map((identity) => ({ ...identity, giftId: ids[identity.role] || '' }));
  };
  const validRealGiftEntries = (entries) => {
    const roles = ['gift_tags', 'gift_dispenser', 'gift_roll'];
    return entries.length === roles.length
      && roles.every((role) => entries.some((entry) => entry.role === role))
      && entries.every((entry) => entry.giftId && Number(entry.productId) > 0 && Number(entry.variantId) > 0)
      && new Set(entries.map((entry) => Number(entry.productId))).size === roles.length
      && new Set(entries.map((entry) => Number(entry.variantId))).size === roles.length
      && new Set(entries.map((entry) => entry.giftId)).size === roles.length;
  };
  const requiredCodesExist = (state, kaching, giftMode = 'transitional_dual') => {
    if (!state || !kaching.deal || !state.barId) return false;
    if (giftMode === 'personalized_real_gifts') {
      return validRealGiftEntries(realGiftEntries(state, kaching, kaching.realGiftIdentities));
    }
    const needsKachingGift = giftMode !== 'companion_only';
    if (state.kind === 'harness_only') return needsKachingGift ? Boolean(kaching.p1Gift) : true;
    if (state.kind === 'harness_bungee') return Boolean(kaching.p2Bungee) && (!needsKachingGift || Boolean(kaching.p2Gift));
    return Boolean(kaching.p3Bungee && kaching.p3Collar) && (!needsKachingGift || Boolean(kaching.p3Gift));
  };
  const controlPlan = ({ deal, state, desiredColor, desiredSize, normalizeColor, normalizeSize, kaching }) => {
    if (state.kind === 'harness_only') {
      return [[desiredColor, normalizeColor], [desiredSize, normalizeSize]];
    }
    const bar = deal?.dealBars?.find((candidate) => candidate.id === state.barId);
    if (!bar || !Array.isArray(bar.bundleProducts)) throw new Error('kaching-variant-plan');
    return bar.bundleProducts.flatMap((item) => {
      if (item.productGID === 'default') {
        return [[desiredColor, normalizeColor], [desiredSize, normalizeSize]];
      }
      const productId = gidNumber(item.productGID);
      if (productId === kaching.collarProductId) {
        return [[desiredColor, normalizeColor], [desiredSize, normalizeSize]];
      }
      if (productId === kaching.bungeeProductId) return [[desiredColor, normalizeColor]];
      throw new Error('kaching-variant-product');
    });
  };
  const validateConfig = ({
    deal,
    state,
    kaching,
    cartGiftProductId,
    realGiftIdentities = [],
    giftMode = 'transitional_dual'
  }) => {
    const needsKachingGift = giftMode !== 'companion_only';
    const usesRealGifts = giftMode === 'personalized_real_gifts';
    const configuredKaching = { ...kaching, realGiftIdentities };
    if (!requiredCodesExist(state, configuredKaching, giftMode)
      || (needsKachingGift && !usesRealGifts && (!cartGiftProductId
        || !kaching.giftProductId
        || cartGiftProductId !== kaching.giftProductId))
      || deal?.nanoId !== kaching.deal
      || !Array.isArray(deal?.dealBars)) return false;
    if (deal.differentVariantsEnabled !== true
      || deal.hideVariantPicker !== false
      || deal.disableVariantOptionSync !== false
      || deal.hideUnavailableOptions !== false
      || deal.showVariantsForSingleQuantity !== true
      || deal.skipCart !== false
      || !Array.isArray(deal.swatchOptions)
      || deal.swatchOptions.length !== 0) return false;
    const bar = deal.dealBars.find((candidate) => candidate.id === state.barId);
    if (!bar || bar.quantitySelector !== false || bar.showAsSoldOutEnabled !== false) return false;
    if (usesRealGifts) {
      const expectedGifts = realGiftEntries(state, kaching, realGiftIdentities);
      if (!validRealGiftEntries(expectedGifts)
        || !Array.isArray(bar.freeGifts)
        || bar.freeGifts.length !== expectedGifts.length) return false;
      if (!expectedGifts.every((expected) => bar.freeGifts.some((gift) => {
        return gift.id === expected.giftId
          && Number(gift.quantity) === 1
          && gidNumber(gift.productGID) === Number(expected.productId);
      }))) return false;
    } else if (needsKachingGift) {
      const expectedGiftId = state.kind === 'full_set' ? kaching.p3Gift : state.kind === 'harness_bungee' ? kaching.p2Gift : kaching.p1Gift;
      if (!Array.isArray(bar.freeGifts) || bar.freeGifts.length !== 1) return false;
      const gift = bar.freeGifts[0];
      if (gift.id !== expectedGiftId
        || Number(gift.quantity) !== 1
        || gidNumber(gift.productGID) !== kaching.giftProductId) return false;
    } else if (bar.freeGifts != null && (!Array.isArray(bar.freeGifts) || bar.freeGifts.length !== 0)) {
      return false;
    }
    if (state.kind === 'harness_only') {
      return bar.dealBarType === 'quantity-break'
        && bar.discountType === 'default'
        && Number(bar.discountValue || 0) === 0
        && Number(bar.quantity) === 1
        && (bar.bundleProducts == null || (Array.isArray(bar.bundleProducts) && bar.bundleProducts.length === 0));
    }
    if (bar.dealBarType !== 'bundle'
      || bar.layout !== 'vertical'
      || bar.showProductsOnlyWhenSelected !== false
      || !Array.isArray(bar.bundleProducts)) return false;
    const expectedItems = state.kind === 'full_set'
      ? [[null, 'default'], [kaching.p3Collar, kaching.collarProductId], [kaching.p3Bungee, kaching.bungeeProductId]]
      : [[null, 'default'], [kaching.p2Bungee, kaching.bungeeProductId]];
    if (bar.bundleProducts.length !== expectedItems.length) return false;
    if (!expectedItems.every(([id, productId]) => bar.bundleProducts.some((item) => {
      const matchesProduct = productId === 'default' ? item.productGID === 'default' : gidNumber(item.productGID) === productId;
      return matchesProduct && (id === null || item.id === id);
    }))) return false;
    return bar.bundleProducts.every((item) => {
      return Number(item.quantity) === 1
        && item.discountType === 'percentage'
        && Number(item.discountValue) === Number(state.discountPercent);
    });
  };
  const supportsItemValidation = (element) => Boolean(
    element
    && typeof element.items === 'function'
    && typeof element.pricing === 'function'
  );
  const validateItems = ({
    element,
    state,
    kaching,
    cartGiftVariantId,
    realGiftIdentities = [],
    giftMode = 'transitional_dual'
  }) => {
    if (!supportsItemValidation(element)) return false;
    let items;
    try {
      items = element.items();
    } catch (error) {
      return false;
    }
    const needsKachingGift = giftMode !== 'companion_only';
    const usesRealGifts = giftMode === 'personalized_real_gifts';
    const paidItemCount = state.kind === 'full_set' ? 3 : state.kind === 'harness_bungee' ? 2 : 1;
    const expectedCount = paidItemCount + (usesRealGifts ? 3 : needsKachingGift ? 1 : 0);
    if (!Array.isArray(items) || items.length !== expectedCount || items.some((item) => Number(item.quantity) !== 1)) return false;
    const mainItem = items.find((item) => Number(item.id) === Number(state.mainVariant.id) && metadataFor(item)?.main === true);
    const mainMetadata = metadataFor(mainItem);
    const returnedBarId = state.kind === 'harness_only' ? mainMetadata?.bid : mainMetadata?.bar;
    const privateGroupId = String(mainMetadata?.id || '').trim();
    if (!mainItem
      || !privateGroupId
      || mainMetadata?.deal !== kaching.deal
      || returnedBarId !== state.barId
      || items.some((item) => String(metadataFor(item)?.id || '').trim() !== privateGroupId)) return false;
    const expectedPaid = [];
    if (state.kind === 'harness_bungee' || state.kind === 'full_set') {
      expectedPaid.push([state.bungeeVariant?.id, state.kind === 'full_set' ? kaching.p3Bungee : kaching.p2Bungee]);
    }
    if (state.kind === 'full_set') expectedPaid.push([state.collarVariant?.id, kaching.p3Collar]);
    const paidItems = expectedPaid.map(([variantId, bundleProduct]) => items.find((item) => {
      const metadata = metadataFor(item);
      return Number(item.id) === Number(variantId)
        && Number(item.parent_id) === Number(state.mainVariant.id)
        && metadata?.deal === kaching.deal
        && metadata?.bar === state.barId
        && metadata?.bundleProduct === bundleProduct;
    }));
    if (paidItems.some((item) => !item) || new Set(paidItems).size !== paidItems.length) return false;
    if (usesRealGifts) {
      const expectedGifts = realGiftEntries(state, kaching, realGiftIdentities);
      if (!validRealGiftEntries(expectedGifts)) return false;
      const giftItems = expectedGifts.map((expected) => items.find((item) => {
        const metadata = metadataFor(item);
        return Number(item.id) === Number(expected.variantId)
          && (!item.product_id || Number(item.product_id) === Number(expected.productId))
          && Number(item.parent_id) === Number(state.mainVariant.id)
          && metadata?.deal === kaching.deal
          && metadata?.gift === expected.giftId;
      }));
      if (giftItems.some((item) => !item)
        || new Set(giftItems).size !== giftItems.length
        || giftItems.some((item) => paidItems.includes(item))) return false;
    } else if (needsKachingGift) {
      const expectedGiftId = state.kind === 'full_set' ? kaching.p3Gift : state.kind === 'harness_bungee' ? kaching.p2Gift : kaching.p1Gift;
      const giftItem = items.find((item) => {
        const metadata = metadataFor(item);
        return Number(item.id) === Number(cartGiftVariantId)
          && Number(item.parent_id) === Number(state.mainVariant.id)
          && metadata?.deal === kaching.deal
          && metadata?.gift === expectedGiftId;
      });
      if (!giftItem || paidItems.includes(giftItem)) return false;
    }
    if (typeof element.pricing !== 'function') return false;
    try {
      const discountedPrice = element.pricing()?.discountedPrice;
      const discountedAmount = discountedPrice && typeof discountedPrice === 'object'
        ? discountedPrice.amount
        : discountedPrice;
      return Math.round(Number(discountedAmount) * 100) === Number(state.total);
    } catch (error) {
      return false;
    }
  };

  window.SafeWalkV51KachingContract = Object.freeze({
    controlPlan,
    readDeal,
    realGiftEntries,
    supportsItemValidation,
    validateConfig,
    validateItems
  });
})();
